import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

interface Props {
  src: string;
  poster: string;
  eyebrow?: string;
}

/**
 * Vertical 9:16 video that auto-plays the moment it scrolls into view
 * and pauses when it leaves. Muted + playsInline so mobile browsers allow autoplay.
 */
const ScrollTriggeredVideo = ({ src, poster, eyebrow }: Props) => {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            v.play().then(() => setHasPlayed(true)).catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {});
  };

  return (
    <section className="relative w-full bg-black overflow-hidden">
      <div className="relative w-full aspect-[9/16] max-h-[88vh]">
        <video
          ref={ref}
          src={src}
          poster={poster}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />

        {eyebrow && (
          <div className="absolute top-4 left-4">
            <span className="inline-block bg-white/10 backdrop-blur-sm border border-white/30 text-white text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full">
              {eyebrow}
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white border border-white/30 backdrop-blur-sm flex items-center justify-center transition-colors"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>

        {!hasPlayed && (
          <div className="absolute inset-x-0 bottom-4 text-center text-white/80 text-xs tracking-wide pointer-events-none">
            Scroll to play · Tap speaker to unmute
          </div>
        )}
      </div>
    </section>
  );
};

export default ScrollTriggeredVideo;